import React from 'react';

/** Small pill-style time badges, e.g. "7:00 AM". */
export const TimesList = ({ label, times = [], testId }) => {
  if (!times.length) return null;
  return (
    <div data-testid={testId}>
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">{label}</p>
      <div className="flex flex-wrap gap-2">
        {times.map((t, i) => (
          <span key={i} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
            {t}
          </span>
        ))}
      </div>
    </div>
  );
};

/** Simple label/price list, e.g. "One Way ... $50.00". */
export const RatesTable = ({ title = 'Rates', rates = [], testId }) => {
  if (!rates.length) return null;
  return (
    <div className="rounded-xl bg-slate-50 p-4" data-testid={testId}>
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">{title}</p>
      <ul className="space-y-1">
        {rates.map(([label, price]) => (
          <li key={label} className="flex items-center justify-between gap-4 text-sm">
            <span className="text-slate-700">{label}</span>
            <span className="font-bold text-slate-900">{price}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Operated by / phone / location / travel-time strip, shared across schedule cards. */
const OperatorStrip = ({ operatedBy, phones = [], location, travelTime, testId }) => {
  if (!operatedBy && !phones.length && !location && !travelTime) return null;
  return (
    <div className="mt-6 grid grid-cols-2 gap-4 rounded-xl bg-slate-50 p-4 text-sm sm:grid-cols-4" data-testid={testId}>
      {operatedBy && (
        <div>
          <span className="block text-slate-500">Operated by</span>
          <span className="font-semibold text-slate-900">{operatedBy}</span>
        </div>
      )}
      {phones.length > 0 && (
        <div>
          <span className="block text-slate-500">Telephone</span>
          {phones.map((p) => (
            <a key={p} href={`tel:${p.replace(/[^0-9+]/g, '')}`} className="block font-semibold text-slate-900 hover:text-red-600 transition-colors">
              {p}
            </a>
          ))}
        </div>
      )}
      {location && (
        <div>
          <span className="block text-slate-500">Location St. John</span>
          <span className="font-semibold text-slate-900">{location}</span>
        </div>
      )}
      {travelTime && (
        <div>
          <span className="block text-slate-500">Travel Time</span>
          <span className="font-semibold text-slate-900">{travelTime}</span>
        </div>
      )}
    </div>
  );
};

/**
 * Section wrapper used for every operator/schedule block on the transportation
 * pages - centered script-red heading over plain white background, matching
 * the Food & Drink / Auto Rentals directory pages (no boxed white cards).
 */
export const ScheduleCard = ({ title, subtitle, children, operator, testId, first = false }) => (
  <section className={`${first ? '' : 'border-t border-slate-200 pt-10'} text-center`} data-testid={testId}>
    {title && <h2 className="font-script text-3xl text-red-600 sm:text-4xl">{title}</h2>}
    {subtitle && <p className="font-script mt-1 text-lg text-slate-700 sm:text-xl">{subtitle}</p>}
    {children && <div className="mt-6 space-y-4 text-left">{children}</div>}
    {operator && <OperatorStrip {...operator} />}
  </section>
);
